import React from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Avatar, 
  Divider,
  Grid,
  Chip,
  Button,
  alpha,
  useTheme
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useAuth } from '../context/AuthContext';
import { AuthGuard } from '../components';

const GlassPaper = styled(Paper)(({ theme }) => ({
  borderRadius: 16,
  backdropFilter: 'blur(16px)',
  background: alpha(theme.palette.background.paper, 0.7),
  border: '1px solid rgba(255, 255, 255, 0.1)',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
  overflow: 'hidden',
  transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
  padding: theme.spacing(4),
}));

const ProfileAvatar = styled(Avatar)(({ theme }) => ({
  width: 120,
  height: 120,
  border: '4px solid rgba(255, 255, 255, 0.2)',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
  marginBottom: theme.spacing(2),
}));

const InfoItem = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(2),
}));

const InfoLabel = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  color: theme.palette.text.secondary,
  fontSize: '0.875rem',
  marginBottom: theme.spacing(0.5),
}));

const InfoValue = styled(Typography)(({ theme }) => ({
  fontWeight: 500,
}));

function Profile() {
  const theme = useTheme();
  const { user, dashboard, logout } = useAuth() || { user: null, dashboard: null, logout: () => {} };
  

  
  const getSubscriptionColor = (tier) => {
    switch (tier) {
      case 'FREE': return 'default';
      case 'PRO': return 'primary';
      case 'ENTERPRISE': return 'warning';
      default: return 'default';
    }
  };
  
  return (
    <AuthGuard>
      <Box sx={{ mt: 2, maxWidth: 1200, mx: 'auto' }}>
        <Typography variant="h4" component="h1" fontWeight="bold" gutterBottom>
          My Profile
        </Typography>
        
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <GlassPaper sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 6 }}>
              <ProfileAvatar src={user?.picture} alt={user?.name}>
                {!user?.picture && (user?.name?.[0] || 'U')}
              </ProfileAvatar>
              
              <Typography variant="h5" fontWeight="bold" gutterBottom>
                {user?.name || 'User'}
              </Typography>
              
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {user?.email || ''}
              </Typography>
              
              <Chip 
                label={user?.subscriptionTier || 'FREE'} 
                color={getSubscriptionColor(user?.subscriptionTier)}
                sx={{ mt: 1 }}
              />
              
              <Button 
                variant="outlined" 
                color="error" 
                sx={{ mt: 4 }}
                onClick={logout}
              >
                Sign Out
              </Button>
            </GlassPaper>
          </Grid>
          
          <Grid item xs={12} md={8}>
            <GlassPaper>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Account Information
              </Typography>
              
              <Divider sx={{ my: 2 }} />
              
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <InfoItem>
                    <InfoLabel>Name</InfoLabel>
                    <InfoValue>{user?.name || 'N/A'}</InfoValue>
                  </InfoItem>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <InfoItem>
                    <InfoLabel>Subscription</InfoLabel>
                    <InfoValue>{user?.subscriptionTier || 'FREE'}</InfoValue>
                  </InfoItem>
                </Grid>
                
                <Grid item xs={12}>
                  <InfoItem>
                    <InfoLabel>Email</InfoLabel>
                    <InfoValue>{user?.email || 'N/A'}</InfoValue>
                  </InfoItem>
                </Grid>
              </Grid>
            </GlassPaper>
          </Grid>
        </Grid>


      </Box>
    </AuthGuard>
  );
}

export default Profile;
